import { apiClient } from "@/lib/api";
import type { DataSourceAdapter, ExcelConfiguration, RowData } from "../types";

/**
 * Transforma as linhas, pede ao servidor o ficheiro e dispara o download no browser.
 * Não toca em estado nem em avisos: quem chama trata do toast e dos erros.
 */
export async function downloadGeneratedExcel<TSource, TRow extends RowData>({
  source,
  adapter,
  configuration,
  dataSourceType,
  entityId,
}: {
  source: TSource;
  adapter: DataSourceAdapter<TSource, TRow>;
  configuration: ExcelConfiguration<TSource>;
  dataSourceType: string;
  entityId: string;
}): Promise<void> {
  // Check if source has edited data
  const sourceWithEdits = source as any;
  let transformedRows: Record<string, any>[];

  if (sourceWithEdits._editedData && Array.isArray(sourceWithEdits._editedData)) {
    // Use edited data directly (already transformed)
    transformedRows = sourceWithEdits._editedData;
  } else {
    // Extract and transform rows normally
    const rows = adapter.extractRows(source);
    transformedRows = rows.map((row) =>
      adapter.transformRow(row, configuration.columns)
    );
  }

  // Call backend API
  const response = await apiClient.axios.post(
    `/excel/generate/${dataSourceType}/${entityId}`,
    {
      configuration: {
        name: configuration.name,
        description: configuration.description,
        columns: configuration.columns,
        options: configuration.options,
        data_source_type: dataSourceType,
      },
      rows: transformedRows,
    },
    { responseType: "blob" }
  );

  // Download file
  const blob = response.data as Blob;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;

  // Get filename from header or use default
  const contentDisposition = response.headers["content-disposition"];
  let filename = configuration.options?.fileName
    ? `${configuration.options.fileName}.xlsx`
    : `${dataSourceType}-${entityId}.xlsx`;

  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?(.+)"?/);
    if (match) filename = match[1];
  }

  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
